import React from 'react';
import axios from 'axios';

import Button from '~/components/core/Atomic/Buttons/Button';
import Container from '~/components/core/Atomic/Container';
import Typography from '~/components/core/Atomic/Typography';
import ConfigurableOption from '~/components/core/Molecules/ConfigurableOption';
import Text from '~/components/core/TextComponents/Text';
import type { Profile } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/phoneProfilesApi';
import { phoneProfileUrls } from '~/components/SystemConfiguration/Communications/PhoneConfigurationTab/phoneProfilesApi';
import { reportAxiosError } from '~/Utils';

import CardDialog from '../../../../CardDialog';
import {
  EditInboundGreetingConfigDialog,
  InboundGreetingConfigView,
} from '../PhoneConfigurations/InboundGreetingConfig';
import {
  EditOutboundGreetingConfigDialog,
  OutboundGreetingConfigView,
} from '../PhoneConfigurations/OutboundGreetingConfig';
import { EditVoicemailConfigDialog, VoicemailConfigView } from '../PhoneConfigurations/VoicemailConfig';
import {
  EditWorkingHoursDialog,
  WorkingHoursConfigView,
} from '../PhoneConfigurations/WorkingHoursConfig/WorkingHoursConfig';

import PhoneProfileDetailHeader from './PhoneProfileDetailHeader';

interface PhoneProfileDetailViewProps {
  profile: Profile;
  organizationId: number;
  orgPhoneSurveyConfigurationId: number | null;
  isLoadingOrgPhoneSurveyConfiguration: boolean;
  onBackToList: () => void;
  reloadProfiles: () => Promise<unknown>;
  reloadOperationalDetails: () => Promise<unknown>;
}

// Per-entry dialogs are heterogeneous in their `configuration` generic; the dialog component +
// submit handler stay loose, everything else is strictly typed so misspelled / missing keys still
// fail at compile time.
type ConfigurationOption = {
  isEnabled?: boolean;
  isHidden?: boolean;
  isToggleDisabled?: boolean;
  onChangeToggle: (isEnabled: boolean) => Promise<void> | void;
  configurationView: React.ReactNode;
  configuration?: unknown;
  configurationKey?: string;
  // ConfigurableOption's strict `=> ReactElement` prop signature doesn't admit FCs that may
  // return null, and per-entry dialog generics differ — widen at this slot only.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  EditConfigurationDialogComponent?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSubmitDialog?: (values: any) => Promise<void> | void;
};

const PhoneProfileDetailView: React.FC<PhoneProfileDetailViewProps> = ({
  profile,
  organizationId,
  orgPhoneSurveyConfigurationId,
  isLoadingOrgPhoneSurveyConfiguration,
  onBackToList,
  reloadProfiles,
  reloadOperationalDetails,
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const patchProfile = async (values: Record<string, unknown>, throwOnError = true): Promise<void> => {
    try {
      setIsSubmitting(true);
      await axios.patch(phoneProfileUrls.resource(organizationId, profile.id), values);
      await reloadProfiles();
    } catch (error) {
      reportAxiosError(error);
      if (throwOnError) {
        throw error;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const putProfileConfig = async (url: string, values: unknown, throwOnError = true): Promise<void> => {
    try {
      setIsSubmitting(true);
      await axios.put(url, values);
      await reloadProfiles();
    } catch (error) {
      reportAxiosError(error);
      if (throwOnError) {
        throw error;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Survey toggle: server-side invariant requires phone_survey_configuration_id to be set when
  // phone_survey_enabled is true. Bundle the FK in the same PATCH on enable; clear it on disable
  // is OK to omit (the FK can remain pointing at the org PSC even when the toggle is off).
  const handleTogglePhoneSurvey = async (newValue: boolean): Promise<void> => {
    if (newValue && orgPhoneSurveyConfigurationId === null) {
      // Guard against enabling without an org PSC; the toggle is gated in UI but double-check here.
      return;
    }
    const payload: Record<string, unknown> = { phone_survey_enabled: newValue };
    if (newValue) {
      payload.phone_survey_configuration_id = orgPhoneSurveyConfigurationId;
    }
    await patchProfile(payload, false);
  };

  const configurationOptions: ConfigurationOption[] = [
    {
      isEnabled: profile.auto_record_calls,
      onChangeToggle: async (newValue: boolean) => await patchProfile({ auto_record_calls: newValue }, false),
      configurationView: (
        <>
          <Typography variant="subtitle1">Automatic call recording</Typography>
          <Typography variant="caption">Record all calls handled by this profile.</Typography>
        </>
      ),
    },
    {
      isEnabled: profile.caller_id_enabled,
      onChangeToggle: async (newValue: boolean) => await patchProfile({ caller_id_enabled: newValue }, false),
      configurationView: (
        <>
          <Typography variant="subtitle1">Enable caller id</Typography>
          <Typography variant="caption">
            Display the caller name on incoming calls, including claims related to the contact
          </Typography>
        </>
      ),
    },
    {
      isEnabled: profile.outbound_call_message_enabled,
      onChangeToggle: async (newValue: boolean) =>
        await patchProfile({ outbound_call_message_enabled: newValue }, false),
      configurationView: <OutboundGreetingConfigView profile={profile} />,
      configuration: profile.outbound_call_message_configuration ?? {},
      EditConfigurationDialogComponent: EditOutboundGreetingConfigDialog,
      onSubmitDialog: async (params: unknown) =>
        await putProfileConfig(phoneProfileUrls.outboundConfig(organizationId, profile.id), params),
    },
    {
      isEnabled: profile.inbound_call_message_enabled,
      onChangeToggle: async (newValue: boolean) =>
        await patchProfile({ inbound_call_message_enabled: newValue }, false),
      configurationView: <InboundGreetingConfigView profile={profile} />,
      configuration: profile.inbound_call_message_configuration ?? {},
      EditConfigurationDialogComponent: EditInboundGreetingConfigDialog,
      onSubmitDialog: async (params: unknown) =>
        await putProfileConfig(phoneProfileUrls.inboundConfig(organizationId, profile.id), params),
    },
    {
      isEnabled: profile.voicemail_message_enabled,
      onChangeToggle: async (newValue: boolean) => await patchProfile({ voicemail_message_enabled: newValue }, false),
      configurationView: <VoicemailConfigView profile={profile} />,
      configuration: {
        voicemail_message_configuration: profile.voicemail_message_configuration,
        out_of_office_message_configuration: profile.out_of_office_message_configuration,
      },
      // Wrapping object always carries both keys; force ConfigurableOption to judge emptiness
      // on the inner FK so the edit dialog auto-opens on first enable when unconfigured.
      configurationKey: 'voicemail_message_configuration',
      EditConfigurationDialogComponent: EditVoicemailConfigDialog,
      onSubmitDialog: async (params: unknown) =>
        await putProfileConfig(phoneProfileUrls.voicemailConfig(organizationId, profile.id), params),
    },
    {
      isEnabled: profile.business_hours_configuration_enabled,
      onChangeToggle: async (newValue: boolean) =>
        await patchProfile({ business_hours_configuration_enabled: newValue }, false),
      configurationView: <WorkingHoursConfigView profile={profile} />,
      configuration: profile.business_hours_configuration ?? {},
      EditConfigurationDialogComponent: EditWorkingHoursDialog,
      onSubmitDialog: async (params: unknown) => await patchProfile(params as Record<string, unknown>, true),
    },
    {
      // Hide while the org PSC fetch is in flight; the toggle's enabled/disabled decision
      // hinges on whether an org PSC exists, and during load we don't know yet — showing
      // "configure at org level first" copy would be misleading.
      isHidden: isLoadingOrgPhoneSurveyConfiguration,
      // Disable the toggle when the org has no PSC; admin must configure the survey at the org
      // level (on the list view) before enabling it on a profile. No edit affordance here —
      // survey content is org-wide.
      isEnabled: profile.phone_survey_enabled,
      isToggleDisabled: orgPhoneSurveyConfigurationId === null,
      onChangeToggle: handleTogglePhoneSurvey,
      configurationView: (
        <>
          <Typography variant="subtitle1">Use organization phone survey</Typography>
          <Typography variant="caption">
            {orgPhoneSurveyConfigurationId === null
              ? 'Configure the phone survey at the organization level first (from the Phone Profiles list).'
              : "Use the organization's configured phone survey for this profile."}
          </Typography>
        </>
      ),
    },
  ];

  return (
    <div className="bg-slate-100 pt-32">
      <Container maxWidth="md">
        <Button color="primary" onClick={onBackToList} className="mb-12">
          <Text variant={Text.VARIANTS.SM}>← Phone Profiles</Text>
        </Button>
        <PhoneProfileDetailHeader
          profile={profile}
          organizationId={organizationId}
          onMutated={reloadProfiles}
          onDeleted={onBackToList}
          reloadOperationalDetails={reloadOperationalDetails}
        />
        <CardDialog width="md" noCardTitle>
          {configurationOptions
            .filter((option) => option?.isHidden !== true)
            .map((option, idx) => (
              <ConfigurableOption
                key={idx}
                isEnabled={!!option.isEnabled}
                onChangeToggle={option.onChangeToggle}
                configurationView={option.configurationView}
                configuration={option.configuration}
                configurationKey={option.configurationKey}
                EditConfigurationDialogComponent={option.EditConfigurationDialogComponent}
                showOnly={isSubmitting || !!option.isToggleDisabled}
                onSubmitDialog={option.onSubmitDialog}
              />
            ))}
        </CardDialog>
      </Container>
    </div>
  );
};

export default PhoneProfileDetailView;
